import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User } from 'app/models/user.models';
import { Role, Permission } from 'app/models/role.models';
import { UserService } from 'app/services/user.service';
import { RoleService } from 'app/services/role.service';
import { NotificacionesService } from 'app/services/notificaciones.service';
import { HttpClient } from '@angular/common/http';
import Swal from 'sweetalert2';
import { UserRoleDialogComponent } from './user-role-dialog/user-role-dialog.component';

// Estructura para agrupar permisos
interface PermissionGroup {
  module: string;
  moduleName: string;
  permissions: Permission[];
  selected: boolean;
}

// Interfaces para el formato del archivo JSON
interface PermissionsData {
  permissionGroups: PermissionGroup[];
  roleTemplates: {
    id: number;
    name: string;
    description: string;
    permissionIds: number[];
  }[];
}

@Component({
  selector: 'user-cmp',
  moduleId: module.id,
  templateUrl: 'user.component.html',
})
export class UserComponent implements OnInit {
  // Variables generales
  currentRoute: string;
  userId: number | null = null;
  roleId: number | null = null;
  isLoading = false;
  notificaciones: any[] = [];
  permissionsData: PermissionsData | null = null;
  allMainRoles = ['admin', 'user', 'cashier'];

  // Variables para lista de usuarios
  @ViewChild('userPaginator') userPaginator: MatPaginator;
  @ViewChild('userSort') userSort: MatSort;
  users: User[] = [];
  userDataSource: MatTableDataSource<User> = new MatTableDataSource<User>([]);
  userDisplayedColumns: string[] = ['fullName', 'email', 'roles', 'active', 'lastLogin', 'acciones'];

  // Variables para formulario de usuario
  userForm: FormGroup;
  isNewUser: boolean = true;
  hidePassword: boolean = true;

  // Variables para lista de roles
  @ViewChild('rolePaginator') rolePaginator: MatPaginator;
  @ViewChild('roleSort') roleSort: MatSort;
  roles: Role[] = [];
  roleDataSource: MatTableDataSource<Role> = new MatTableDataSource<Role>([]);
  roleDisplayedColumns: string[] = ['name', 'description', 'permissionsCount', 'acciones'];

  // Variables para formulario de rol
  roleForm: FormGroup;
  isNewRole: boolean = true;
  allPermissions: Permission[] = [];
  selectedPermissions: Permission[] = [];
  permissionGroups: PermissionGroup[] = [];
  selectedTemplateId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private roleService: RoleService,
    private notificacionesService: NotificacionesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initForms();
    this.getNotificaciones();

    // Detectar la ruta actual para mostrar el componente correspondiente
    this.route.url.subscribe(segments => {
      const url = this.router.url;

      if (url.includes('/users/edit/')) {
        this.currentRoute = 'user-form';
        this.isNewUser = false;
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.userId = +id;
          this.loadUser(this.userId);
        }
      } else if (url.includes('/users/new')) {
        this.currentRoute = 'user-form';
        this.isNewUser = true;
        // Cargar roles para el formulario de creación de usuario
        this.loadRoles();
      } else if (url.includes('/roles/edit/')) {
        this.currentRoute = 'role-form';
        this.isNewRole = false;
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.roleId = +id;
          this.loadRole(this.roleId);
        }
      } else if (url.includes('/roles/new')) {
        this.currentRoute = 'role-form';
        this.isNewRole = true;
        this.loadPermissions();
        // Cuando el JSON de permisos esté cargado, seleccionaremos la primera plantilla
        setTimeout(() => {
          if (
            this.permissionsData &&
            this.permissionsData.roleTemplates &&
            this.permissionsData.roleTemplates.length > 0
          ) {
            this.applyRoleTemplate(this.permissionsData.roleTemplates[0].id);
          }
        }, 500);
      } else if (url.includes('/roles')) {
        this.currentRoute = 'role-list';
        this.loadRoles();
      } else {
        this.currentRoute = 'user-list';
        this.loadUsers();
      }
    });
  }

  // Inicialización de formularios
  initForms() {
    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      password: ['', this.isNewUser ? [Validators.required, Validators.minLength(6)] : []],
      active: [true],
      roleId: [null, [Validators.required]],
    });

    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required]],
    });
  }

  // #region Métodos para la gestión de usuarios
  loadUsers() {
    this.isLoading = true;
    this.userService.getUsers().subscribe({
      next: (response: any) => {
        // Comprobamos si la respuesta tiene la estructura esperada con 'users' y 'total'
        const users = Array.isArray(response) ? response : response.users || [];
        this.users = users;
        this.userDataSource = new MatTableDataSource(users);
        this.userDataSource.paginator = this.userPaginator;
        this.userDataSource.sort = this.userSort;
        this.isLoading = false;
      },
      error: error => {
        console.error('Error cargando usuarios:', error);
        this.isLoading = false;
        this.snackBar.open('Error al cargar la lista de usuarios', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  loadUser(id: number) {
    this.isLoading = true;
    // Primero cargar los roles disponibles
    this.loadRoles();

    this.userService.getUser(id).subscribe({
      next: user => {
        // Extraer el roleId del primer rol del usuario (si existe)
        const roleId = user.roles && user.roles.length > 0 ? user.roles[0].id : null;

        this.userForm.patchValue({
          email: user.email,
          name: user.name || '',
          lastname: user.lastname || '',
          active: user.active,
          roleId: roleId,
        });
        this.isLoading = false;
      },
      error: error => {
        console.error('Error cargando usuario:', error);
        this.isLoading = false;
        this.snackBar.open('Error al cargar los datos del usuario', 'Cerrar', {
          duration: 3000,
        });
        this.router.navigate(['/configurar/colaboradores/users']);
      },
    });

    if (this.isNewUser) {
      this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
    } else {
      this.userForm.get('password')?.clearValidators();
      this.userForm.get('password')?.setValidators(Validators.minLength(6));
    }
    this.userForm.get('password')?.updateValueAndValidity();
  }

  submitUserForm() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const userData = this.userForm.value;

    // Si no es un nuevo usuario y la contraseña está vacía, la eliminamos
    if (!this.isNewUser && !userData.password) {
      delete userData.password;
    }

    delete userData.password;

    if (this.isNewUser) {
      this.createUser(userData);
    } else {
      this.updateUser(userData);
    }
  }

  createUser(userData: any) {
    // Extraer el roleId del formulario
    const roleId = userData.roleId;
    delete userData.roleId;

    // Generar el nombre de usuario a partir del email
    const username = userData.email.split('@')[0];

    // Preparar el payload en el formato requerido
    const payload = {
      email: userData.email,
      password: userData.password,
      username: username,
      name: userData.name,
      lastname: userData.lastname,
      active: userData.active,
      roleIds: roleId ? [roleId] : [], // Enviar como array de números
    };

    // Crear el usuario con el formato requerido
    this.userService.createUser(payload).subscribe({
      next: newUser => {
        this.snackBar.open('Usuario creado con éxito', 'Cerrar', { duration: 3000 });
        this.router.navigate(['/configurar/colaboradores/users']);
      },
      error: error => {
        console.error('Error creando usuario:', error);
        this.snackBar.open('Error al crear el usuario', 'Cerrar', { duration: 3000 });
        this.isLoading = false;
      },
    });
  }

  updateUser(userData: any) {
    if (!this.userId) return;

    // Extraer el roleId del formulario
    const roleId = userData.roleId;
    delete userData.roleId;

    // Para actualizar, obtenemos primero el usuario para mantener el username original
    this.userService.getUser(this.userId).subscribe({
      next: currentUser => {
        // Preparar el payload en el formato requerido
        const payload = {
          email: userData.email,
          name: userData.name,
          lastname: userData.lastname,
          active: userData.active,
          // Mantener el username original o generarlo del email si no existe
          username: currentUser.username || userData.email.split('@')[0],
          roleIds: roleId ? [roleId] : [], // Enviar como array de números
        };

        // Añadir la contraseña solo si se ha proporcionado una nueva
        if (userData.password) {
          payload['password'] = userData.password;
        }

        // Actualizar los datos del usuario
        this.userService.updateUser(this.userId, payload).subscribe({
          next: () => {
            this.snackBar.open('Usuario actualizado con éxito', 'Cerrar', {
              duration: 3000,
            });
            this.router.navigate(['/configurar/colaboradores/users']);
          },
          error: error => {
            console.error('Error actualizando usuario:', error);
            this.snackBar.open('Error al actualizar el usuario', 'Cerrar', {
              duration: 3000,
            });
            this.isLoading = false;
          },
        });
      },
      error: error => {
        console.error('Error obteniendo usuario para actualizar:', error);
        this.snackBar.open('Error al actualizar el usuario', 'Cerrar', {
          duration: 3000,
        });
        this.isLoading = false;
      },
    });
  }

  // Método auxiliar para actualizar el rol de un usuario
  updateUserRole(userId: number, newRoleId: number, user: User) {
    // Si ya tiene roles, revisar si necesitamos actualizar
    if (user.roles && user.roles.length > 0) {
      const currentRoleId = user.roles[0].id;

      // Si el rol ya está asignado, no hacemos nada más
      if (currentRoleId === newRoleId) {
        this.snackBar.open('Usuario actualizado con éxito', 'Cerrar', { duration: 3000 });
        this.router.navigate(['/configurar/colaboradores/users']);
        return;
      }

      // Si tiene un rol diferente, quitar el anterior y asignar el nuevo
      this.userService.removeRoleFromUser(userId, currentRoleId).subscribe({
        next: () => {
          this.userService.assignRoleToUser(userId, newRoleId).subscribe({
            next: () => {
              this.snackBar.open('Usuario y rol actualizados con éxito', 'Cerrar', {
                duration: 3000,
              });
              this.router.navigate(['/configurar/colaboradores/users']);
            },
            error: roleError => {
              console.error('Error asignando nuevo rol:', roleError);
              this.snackBar.open('Usuario actualizado, pero hubo un error al asignar el nuevo rol', 'Cerrar', {
                duration: 3000,
              });
              this.router.navigate(['/configurar/colaboradores/users']);
            },
          });
        },
        error: roleError => {
          console.error('Error eliminando rol anterior:', roleError);
          this.snackBar.open('Usuario actualizado, pero hubo un error al actualizar el rol', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/configurar/colaboradores/users']);
        },
      });
    } else {
      // Si no tiene roles, asignar el nuevo directamente
      this.userService.assignRoleToUser(userId, newRoleId).subscribe({
        next: () => {
          this.snackBar.open('Usuario actualizado y rol asignado con éxito', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/configurar/colaboradores/users']);
        },
        error: roleError => {
          console.error('Error asignando rol:', roleError);
          this.snackBar.open('Usuario actualizado, pero hubo un error al asignar el rol', 'Cerrar', { duration: 3000 });
          this.router.navigate(['/configurar/colaboradores/users']);
        },
      });
    }
  }

  cancelUserForm() {
    this.router.navigate(['/configurar/colaboradores/users']);
  }

  goToEditUser(user: User) {
    this.router.navigate(['/configurar/colaboradores/users/edit', user.id]);
  }

  goToCreateUser() {
    this.router.navigate(['/configurar/colaboradores/users/new']);
  }

  deleteUser(user: User) {
    Swal.fire({
      title: '¿Estás seguro de eliminar este usuario?',
      text: `${user.fullName} (${user.username}) será eliminado permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        this.userService.deleteUser(user.id).subscribe({
          next: () => {
            this.loadUsers();
            this.snackBar.open('Usuario eliminado correctamente', 'Cerrar', {
              duration: 3000,
            });
          },
          error: error => {
            console.error('Error al eliminar usuario:', error);
            this.snackBar.open('Error al eliminar el usuario', 'Cerrar', {
              duration: 3000,
            });
          },
        });
      }
    });
  }

  toggleUserStatus(user: User) {
    const newStatus = !user.active;
    const action = newStatus ? 'activar' : 'desactivar';

    Swal.fire({
      title: `¿Estás seguro de ${action} este usuario?`,
      text: `${user.fullName ?? user.name} (${user.username}) será ${newStatus ? 'activado' : 'desactivado'}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, confirmar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        this.userService.updateUser(user.id, { active: newStatus }).subscribe({
          next: () => {
            user.active = newStatus;
            this.snackBar.open(`Usuario ${newStatus ? 'activado' : 'desactivado'} correctamente`, 'Cerrar', {
              duration: 3000,
            });
          },
          error: error => {
            console.error(`Error al ${action} usuario:`, error);
            this.snackBar.open(`Error al ${action} el usuario`, 'Cerrar', {
              duration: 3000,
            });
          },
        });
      }
    });
  }

  manageUserRoles(user: User) {
    const dialogRef = this.dialog.open(UserRoleDialogComponent, {
      width: '400px',
      height: 'auto',
      panelClass: 'custom-modal',
      autoFocus: false,
      data: {
        name: user.name,
        rolesFiltrados: this.getFilteredRoles(user.rol),
      },
    });

    dialogRef.afterClosed().subscribe((newRole: string) => {
      if (newRole) {
        this.updateUserMainRole(user.id, newRole);
      }
    });
  }

  private getFilteredRoles(currentRole: string): string[] {
    return this.allMainRoles.filter(role => role !== currentRole);
  }
  private updateUserMainRole(userId: number, newRole: string): void {
    this.userService.updateUser(userId, { rol: newRole }).subscribe({
      next: () => {
        const userIndex = this.users.findIndex(u => u.id === userId);
        if (userIndex !== -1) {
          this.users[userIndex].rol = newRole;
        }
        console.log('Rol actualizado con éxito');
        this.snackBar.open('Rol actualizado exitosamente', 'Éxito', {
          duration: 3000,
        });
      },
      error: err => {
        console.error('Error al actualizar el rol:', err);
        this.snackBar.open('Error al actualizar el rol', 'Error', {
          duration: 3000,
        });
      },
    });
  }

  getRolesString(user: User): string {
    if (!user.roles || user.roles.length === 0) {
      return 'Sin roles asignados';
    }
    return user.roles.map(role => role.name).join(', ');
  }

  applyUserFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.userDataSource.filter = filterValue.trim().toLowerCase();

    if (this.userDataSource.paginator) {
      this.userDataSource.paginator.firstPage();
    }
  }
  // #endregion

  // #region Métodos para la gestión de roles
  loadRoles() {
    this.isLoading = true;
    this.roleService.getRoles().subscribe({
      next: roles => {
        this.roles = roles;
        this.roleDataSource = new MatTableDataSource(roles);
        this.roleDataSource.paginator = this.rolePaginator;
        this.roleDataSource.sort = this.roleSort;
        this.isLoading = false;
      },
      error: error => {
        console.error('Error cargando roles:', error);
        this.isLoading = false;
        this.snackBar.open('Error al cargar la lista de roles', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  loadPermissions(callback?: () => void) {
    // Cargar permisos desde archivo JSON local
    this.http.get<PermissionsData>('assets/data/permissions.json').subscribe({
      next: data => {
        this.permissionsData = data;

        // Asignar los permisos desde los grupos
        let allPermissions: Permission[] = [];
        data.permissionGroups.forEach(group => {
          allPermissions = [...allPermissions, ...group.permissions];
        });
        this.allPermissions = allPermissions;

        // Crear grupos de permisos para la UI
        this.permissionGroups = data.permissionGroups.map(group => ({
          ...group,
          selected: false,
        }));

        // Si estamos editando un rol, actualizar las selecciones
        if (!this.isNewRole && this.selectedPermissions.length > 0) {
          this.updatePermissionGroupsSelection();
        }

        // Ejecutar el callback si existe
        if (callback) {
          callback();
        }
      },
      error: error => {
        console.error('Error cargando permisos desde el archivo JSON:', error);
        this.snackBar.open('Error al cargar los permisos disponibles', 'Cerrar', {
          duration: 3000,
        });
        // Ejecutar el callback aunque haya error, para evitar bloqueos
        if (callback) {
          callback();
        }
      },
    });
  }

  loadRole(roleId: number) {
    this.isLoading = true;
    this.loadPermissions(() => {
      this.roleService.getRole(roleId).subscribe({
        next: role => {
          this.roleForm.patchValue({
            name: role.name,
            description: role.description,
          });

          // Sincronizar los permisos seleccionados con los cargados
          this.selectedPermissions = this.allPermissions.filter(p =>
            (role.permissions || []).some(rp => rp.id === p.id)
          );

          // Si no hay permisos pero el nombre del rol coincide con una plantilla, aplicar esos permisos
          if (this.selectedPermissions.length === 0 && this.permissionsData) {
            const matchingTemplateByName = this.permissionsData.roleTemplates.find(
              template => template.name.toLowerCase() === role.name.toLowerCase()
            );

            if (matchingTemplateByName) {
              // Aplicar los permisos de la plantilla
              this.selectedPermissions = this.allPermissions.filter(permission =>
                matchingTemplateByName.permissionIds.includes(permission.id)
              );
            }
          }

          // Marcar los permisos que ya tiene asignados el rol
          this.updatePermissionGroupsSelection();

          // Intentar identificar qué plantilla corresponde a este rol
          const template = this.identifyRoleTemplateAndSet();

          if (template) {
            // Solo marcar la plantilla seleccionada para la UI, NO sobrescribir los datos del rol
            this.selectedTemplateId = template.id;
            // Forzar detección de cambios
            this.cdr.detectChanges();
            // Timeout adicional para asegurar que Angular actualice la UI
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 100);
          } else {
            this.selectedTemplateId = null;
          }

          this.isLoading = false;
        },
        error: error => {
          console.error('Error cargando el rol:', error);
          this.snackBar.open('Error al cargar los datos del rol', 'Cerrar', {
            duration: 3000,
          });
          this.isLoading = false;
          this.router.navigate(['/configurar/colaboradores/roles']);
        },
      });
    });
  }

  submitRoleForm() {
    if (this.roleForm.invalid) {
      this.roleForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const roleData = {
      ...this.roleForm.value,
      permissions: this.selectedPermissions.map(p => p.id),
    };

    if (this.isNewRole) {
      this.createRole(roleData);
    } else {
      this.updateRole(roleData);
    }
  }

  createRole(roleData: any) {
    this.roleService.createRole(roleData).subscribe({
      next: () => {
        this.snackBar.open('Rol creado con éxito', 'Cerrar', { duration: 3000 });
        this.router.navigate(['/configurar/colaboradores/roles']);
      },
      error: error => {
        console.error('Error creando el rol:', error);
        this.snackBar.open('Error al crear el rol', 'Cerrar', { duration: 3000 });
        this.isLoading = false;
      },
    });
  }

  updateRole(roleData: any) {
    if (!this.roleId) return;

    this.roleService.updateRole(this.roleId, roleData).subscribe({
      next: () => {
        this.snackBar.open('Rol actualizado con éxito', 'Cerrar', { duration: 3000 });
        this.router.navigate(['/configurar/colaboradores/roles']);
      },
      error: error => {
        console.error('Error actualizando el rol:', error);
        this.snackBar.open('Error al actualizar el rol', 'Cerrar', { duration: 3000 });
        this.isLoading = false;
      },
    });
  }

  cancelRoleForm() {
    this.router.navigate(['/configurar/colaboradores/roles']);
  }

  goToEditRole(role: Role) {
    this.router.navigate(['/configurar/colaboradores/roles/edit', role.id]);
  }

  goToCreateRole() {
    this.router.navigate(['/configurar/colaboradores/roles/new']);
  }

  deleteRole(role: Role) {
    Swal.fire({
      title: '¿Estás seguro de eliminar este rol?',
      text: `El rol "${role.name}" será eliminado permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        this.roleService.deleteRole(role.id).subscribe({
          next: () => {
            this.loadRoles();
            this.snackBar.open('Rol eliminado correctamente', 'Cerrar', {
              duration: 3000,
            });
          },
          error: error => {
            console.error('Error al eliminar rol:', error);
            this.snackBar.open('Error al eliminar el rol', 'Cerrar', { duration: 3000 });
          },
        });
      }
    });
  }

  applyRoleFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.roleDataSource.filter = filterValue.trim().toLowerCase();

    if (this.roleDataSource.paginator) {
      this.roleDataSource.paginator.firstPage();
    }
  }

  // Manejo de permisos
  togglePermission(permission: Permission) {
    const index = this.selectedPermissions.findIndex(p => p.id === permission.id);
    if (index === -1) {
      // Si no está seleccionado, lo agregamos
      this.selectedPermissions.push(permission);
    } else {
      // Si ya está seleccionado, lo quitamos
      this.selectedPermissions.splice(index, 1);
    }

    // Actualizar estado del grupo
    this.updateGroupSelectionState(permission.module);
  }

  updateGroupSelectionState(moduleName: string) {
    const group = this.permissionGroups.find(g => g.module === moduleName);
    if (!group) return;

    // Verificar si todos los permisos del grupo están seleccionados
    group.selected = group.permissions.every(permission => this.selectedPermissions.some(p => p.id === permission.id));
  }

  toggleGroup(group: PermissionGroup) {
    group.selected = !group.selected;

    if (group.selected) {
      // Si se selecciona el grupo, agregar todos los permisos que no estén ya seleccionados
      group.permissions.forEach(permission => {
        if (!this.isPermissionSelected(permission)) {
          this.selectedPermissions.push(permission);
        }
      });
    } else {
      // Si se deselecciona el grupo, quitar todos sus permisos
      this.selectedPermissions = this.selectedPermissions.filter(p => p.module !== group.module);
    }
  }

  updatePermissionGroupsSelection() {
    if (!this.permissionGroups.length || !this.selectedPermissions.length) return;

    // Para cada grupo, verificar si todos sus permisos están seleccionados
    this.permissionGroups.forEach(group => {
      const allPermissionsInGroupSelected = group.permissions.every(permission =>
        this.selectedPermissions.some(p => p.id === permission.id)
      );
      group.selected = allPermissionsInGroupSelected;
    });
  }

  isPermissionSelected(permission: Permission) {
    return this.selectedPermissions.some(p => p.id === permission.id);
  }

  selectAllPermissions() {
    if (this.permissionGroups.every(g => g.selected)) {
      // Si todos están seleccionados, deseleccionar todos
      this.permissionGroups.forEach(g => (g.selected = false));
      this.selectedPermissions = [];
    } else {
      // Seleccionar todos
      this.permissionGroups.forEach(g => (g.selected = true));
      this.selectedPermissions = [...this.allPermissions];
    }
  }

  // Método para obtener el texto del botón "Seleccionar todos"
  getSelectAllButtonText(): string {
    return this.permissionGroups.every(group => group.selected) ? 'Deseleccionar todos' : 'Seleccionar todos';
  }

  // Método para aplicar una plantilla de rol preestablecida
  applyRoleTemplate(templateId: number) {
    if (!this.permissionsData) return;

    const template = this.permissionsData.roleTemplates.find(t => t.id === templateId);
    if (!template) return;

    // Establecer los datos básicos del rol
    this.roleForm.patchValue({
      name: template.name,
      description: template.description,
    });

    // Aplicar los permisos de la plantilla
    this.selectedPermissions = this.allPermissions.filter(permission => template.permissionIds.includes(permission.id));

    // Actualizar la selección en los grupos de permisos
    this.updatePermissionGroupsSelection();

    // Marcar la plantilla seleccionada para la UI
    this.selectedTemplateId = templateId;

    this.snackBar.open('Plantilla de rol aplicada correctamente', 'Cerrar', {
      duration: 3000,
    });
  }

  // Método para identificar si el rol editado corresponde a una plantilla
  /**
   * Identifica si el rol editado corresponde a una plantilla y la retorna si coincide.
   * Si no coincide, retorna null.
   */
  identifyRoleTemplateAndSet(): any {
    if (!this.permissionsData || !this.selectedPermissions.length) {
      return null;
    }

    // Obtener los IDs de los permisos seleccionados
    const selectedPermissionIds = this.selectedPermissions.map(p => p.id).sort((a, b) => a - b);

    // Buscar la plantilla que coincida exactamente con los permisos seleccionados
    const matchingTemplate = this.permissionsData.roleTemplates.find(template => {
      const templatePermissionIds = [...template.permissionIds].sort((a, b) => a - b);

      if (template.permissionIds.length !== selectedPermissionIds.length) {
        return false;
      }

      return template.permissionIds.every(id => selectedPermissionIds.includes(id));
    });

    return matchingTemplate || null;
  }

  // Método para obtener el color del checkbox según el tipo de permiso
  getPermissionColor(permission: Permission): 'primary' | 'accent' | 'warn' {
    switch (permission.action) {
      case 'read':
        return 'primary'; // Azul para permisos de lectura
      case 'write':
        return 'accent'; // Verde/Naranja para permisos de escritura
      case 'delete':
        return 'warn'; // Rojo para permisos de eliminación
      default:
        return 'primary';
    }
  }

  // Función trackBy para las plantillas
  trackByTemplateId(index: number, template: any): number {
    return template.id;
  }

  // #endregion

  // Método para generar una contraseña segura
  generateSecurePassword() {
    // Definir los caracteres a usar para cada tipo
    const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*()_-+=<>?';

    // Configuración de la contraseña
    const passwordLength = 12;

    // Asegurarse de que la contraseña tenga al menos uno de cada tipo
    let password = '';

    // Añadir un caracter de cada tipo para garantizar requisitos mínimos
    password += lowerCaseChars.charAt(Math.floor(Math.random() * lowerCaseChars.length));
    password += upperCaseChars.charAt(Math.floor(Math.random() * upperCaseChars.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));

    // Todos los caracteres posibles para el resto de la contraseña
    const allChars = lowerCaseChars + upperCaseChars + numbers + specialChars;

    // Completar la contraseña con caracteres aleatorios
    for (let i = password.length; i < passwordLength; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    // Mezclar los caracteres para que no siempre sigan el mismo patrón
    password = this.shuffleString(password);

    // Mostrar la contraseña
    this.hidePassword = false; // Mostrar la contraseña generada
    this.userForm.patchValue({ password: password });

    // Notificar al usuario
    this.snackBar.open('Contraseña segura generada', 'Cerrar', { duration: 3000 });
  }

  // Método auxiliar para mezclar los caracteres de un string
  shuffleString(str: string): string {
    const array = str.split('');
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]]; // Intercambio
    }
    return array.join('');
  }

  // Método para navegar entre gestión de usuarios y roles
  navigateToSection(section: string) {
    if (section === 'users') {
      this.router.navigate(['/configurar/colaboradores/users']);
    } else if (section === 'roles') {
      this.router.navigate(['/configurar/colaboradores/roles']);
    }
  }

  // Método para eliminar notificaciones
  deleteNotificacion(id: any) {
    this.notificacionesService.deleteNotificacion(id).subscribe({
      next: () => {
        this.snackBar.open('Notificación eliminada correctamente', 'Cerrar', {
          duration: 3000,
        });
        this.getNotificaciones(); // Recargar las notificaciones
      },
      error: error => {
        console.error('Error al eliminar la notificación:', error);
        this.snackBar.open('Error al eliminar la notificación', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  // Getters para los formularios
  get emailControl() {
    return this.userForm.get('email');
  }
  get nameUserControl() {
    return this.userForm.get('name');
  }
  get lastnameControl() {
    return this.userForm.get('lastname');
  }
  get passwordControl() {
    return this.userForm.get('password');
  }
  get roleIdControl() {
    return this.userForm.get('roleId');
  }
  get nameControl() {
    return this.roleForm.get('name');
  }
  get descriptionControl() {
    return this.roleForm.get('description');
  }

  // Método para obtener notificaciones
  getNotificaciones() {
    this.notificacionesService.getNotificaciones().subscribe({
      next: notificaciones => {
        this.notificaciones = notificaciones;
      },
      error: error => {
        console.error('Error al obtener notificaciones:', error);
      },
    });
  }
}
